import React, { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Collapse,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { pulse } from "./job-detail-constants";
import { formatStepDuration, type TaskStep } from "./job-detail-helpers";

/** Status icon for a task step (mirrors GitHub Actions' step status icons). */
export function StepStatusIcon({ status }: { status: TaskStep["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />;
    case "in_progress":
      return (
        <SyncIcon
          sx={{
            fontSize: 18,
            color: "info.main",
            animation: `${pulse} 1.5s infinite`,
          }}
        />
      );
    case "queued":
      return <HourglassEmptyIcon sx={{ fontSize: 18, color: "grey.500" }} />;
    case "skipped":
      return <ChevronRightIcon sx={{ fontSize: 18, color: "grey.400" }} />;
    default:
      return (
        <HourglassEmptyIcon sx={{ fontSize: 18, color: "warning.main" }} />
      );
  }
}

/**
 * TaskStepsList — GitHub Actions-style collapsible step list with inline logs.
 * Each step is a row with status icon, label, duration.
 * Click to expand/collapse and see the step's log output.
 */
export function TaskStepsList({
  steps,
  defaultExpandedKey,
}: {
  steps: TaskStep[];
  /** Key of a step to auto-expand (e.g. the failed step) */
  defaultExpandedKey?: string;
}): React.ReactElement {
  const theme = useTheme();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => {
    if (defaultExpandedKey) return new Set([defaultExpandedKey]);
    // Auto-expand the first failed or in_progress step
    const firstActive = steps.find(
      (s) => s.status === "failed" || s.status === "in_progress",
    );
    return firstActive ? new Set([firstActive.key]) : new Set();
  });

  const toggleStep = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (steps.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
        No tasks yet
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {steps.map((step) => {
        const isExpanded = expandedKeys.has(step.key);
        const hasLogs = step.logLines && step.logLines.length > 0;
        return (
          <Box key={step.key}>
            {/* Step header row */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => toggleStep(step.key)}
              sx={{
                py: 1,
                px: 2,
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
              }}
            >
              <StepStatusIcon status={step.status} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{
                    color:
                      step.status === "failed" ? "error.main" : "text.primary",
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {formatStepDuration(step.startedAt, step.completedAt) && (
                <Typography variant="caption" color="text.secondary">
                  {formatStepDuration(step.startedAt, step.completedAt)}
                </Typography>
              )}
              {step.htmlUrl && (
                <Typography
                  variant="caption"
                  component="a"
                  href={step.htmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ color: "primary.main", textDecoration: "underline" }}
                >
                  View
                </Typography>
              )}
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: "text.disabled",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </Stack>

            {/* Expandable log output */}
            <Collapse in={isExpanded} timeout={200}>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: "auto",
                  bgcolor: alpha("#000000", 0.03),
                  px: 2,
                  py: 1,
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  lineHeight: 1.6,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                }}
              >
                {hasLogs ? (
                  step.logLines!.map((line, i) => (
                    <Typography
                      key={i}
                      variant="inherit"
                      component="div"
                      sx={{
                        color:
                          line.toLowerCase().includes("error") ||
                          line.toLowerCase().includes("failed")
                            ? "error.main"
                            : line.toLowerCase().includes("warn")
                              ? "warning.main"
                              : "text.secondary",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {step.status === "in_progress"
                      ? "Running..."
                      : step.status === "pending" || step.status === "queued"
                        ? "Waiting to start..."
                        : "No log output"}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}
